import { Injectable } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { CacheService } from '../../sharedServices/cache.service';
import { ComparatorEnum } from '../../models/interface/comparator.enum';
import { PermissionLoadDataService } from './permission-load-data.service';
import { PermissionBaseType } from './models/permissionBaseType.enum';
import { UserDataService } from '../../sharedServices/user-data.service';
import { PermissionUserOption } from './keys/permissionUser.interface';
import { PermissionShared } from './models/permission.interface';
import { PermissionOperator } from './models/permissionOperator.interface';
import { PermissionOption } from './models/permissionOption.interface';
import { PermissionLoadConfigShared } from '../../models/interface/permissionLoadConfig.interface';
import { allPermissionOptionList } from '../../sharedConstants/menu.constants';

export type PermissionsCheck = { [key: string]: boolean };

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  /**
   * Make can access cacheable until browser is refreshed (memory store)
   */
  canAccess = this.cacheService.cache(this, {
    keyPrefix: 'canAccess',
    keyGenerator: (option: string) => `${option}`,
    generator: this.canAccessCall,
  });

  private permissionList: PermissionShared[];
  private flagList: { [key: string]: boolean } = {};
  private allFlagList: PermissionOption[] = [];

  private allPermissionList: PermissionOption[] = allPermissionOptionList;

  constructor(private cacheService: CacheService, private loadService: PermissionLoadDataService, userDataService: UserDataService) {
    userDataService.getTokenLocal().subscribe(async _token => {
      await this.canAccess(PermissionUserOption.USER_COMMON_FLAG_LOG_GRPC);
    });
  }

  /**
   * method to get permissionList
   *
   * @return {PermissionShared[]} response
   */
  async getPermissionList(config?: PermissionLoadConfigShared): Promise<PermissionShared[]> {
    this.permissionList = await this.loadService.loadPermissionListByUser(config);
    this.flagList = await this.loadService.loadFlagList(config);
    return this.permissionList;
  }

  /**
   * reset the permission store to
   */
  reset(): void {
    this.permissionList = null;
    this.flagList = null;
    this.loadService.reset();
  }

  /**
   * method to know if user have permission to use specific resource (list)
   *
   * @param {string[]} permissionKeyList
   * @return {PermissionsCheck} response
   */
  async canAccessPermissions(...permissionKeyList: string[]): Promise<PermissionsCheck> {
    const permissionList = await this.getPermissionList();
    const response: PermissionsCheck = {};
    for (const permission of permissionKeyList) {
      response[permission] = this.checkPermission(permission, permissionList);
    }
    return response;
  }

  /**
   * method to check permission
   *
   * @param {string} option Permission to check
   * @param {PermissionShared[]} permissionList
   * @return {boolean} response
   */
  private checkPermission(option: string, permissionList: PermissionShared[]): boolean {
    if (environment.shared.enableAllPermissions) {
      return true;
    }

    if (!option || !permissionList || permissionList?.length === 0) {
      return false;
    }
    this.allFlagList = this.allPermissionList.filter(x => x.type === PermissionBaseType.FLAG);
    if (this.allFlagList.filter(x => x.permissions.includes(option)).length > 0) {
      return this.flagList[option];
    }

    const sourcePermission: PermissionOption = this.getSourcePermission(option);

    if (!sourcePermission) {
      return false;
    }

    if (sourcePermission.related) {
      for (const permissionRelated of sourcePermission.related) {
        const checkRelatedPermission = this.checkPermission(permissionRelated, permissionList);

        if (!checkRelatedPermission) {
          return false;
        }
      }
    }

    const canActivate: boolean = this.getCanActivate(sourcePermission, permissionList);

    if (sourcePermission.hideAction === true) {
      return !canActivate;
    }
    return canActivate;
  }

  /**
   * get source permission
   * @param {string} option
   * @returns {PermissionOption} response
   */
  getSourcePermission(option: string): PermissionOption {
    return this.allPermissionList.find(x => {
      switch (x.comparator) {
        case ComparatorEnum.EXACT:
          return option === x.option;
        case ComparatorEnum.CONTAINS:
          return option.includes(x.option);
        case ComparatorEnum.STARTS_WITH:
        default:
          return option.startsWith(x.option);
      }
    });
  }

  /**
   * get can activate
   * @param sourcePermission
   * @param permissionList
   * @returns {boolean} response
   */
  getCanActivate(sourcePermission: PermissionOption, permissionList: PermissionShared[]): boolean {
    let canActivate: boolean;
    for (const permissionType of sourcePermission.permissions) {
      const permission: string[] = permissionType.split('.');
      const result = permissionList.filter(x => x.application === permission[0] && x.module === permission[1] && x.type === permission[2]);
      canActivate = result.length > 0;
      if (sourcePermission.operator === PermissionOperator.OR) {
        if (canActivate) {
          break;
        }
        continue;
      }
      if (!canActivate) {
        break;
      }
    }
    return canActivate;
  }

  /**
   * method to know if user have permission to use specific resource
   *
   * @param {string} option Option name to find permission in api
   * @return {boolean} response
   */
  private async canAccessCall(option: string): Promise<boolean> {
    const permissionList = await this.getPermissionList();
    return this.checkPermission(option, permissionList);
  }
}
